import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';
import { api } from './api';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * useServerList — server-side list state for the generic-CRUD list pages
 * (ParcelsList / ClientsList / PropertiesList, PRD 06.1).
 *
 * The backend generic-CRUD GET handler (src/routes/crud.js) accepts
 * `q` (LIKE over the table's SEARCH_COLUMNS), `sort`+`dir` (whitelisted real
 * column, server-side ORDER BY), `page`+`limit` (opt-in paginated envelope
 * `{ rows, total, page, limit }`), and scalar filters (status/re_pp/under_appeal…)
 * — ALL enforced server-side under the same org + SoD row-scope as the bare list.
 * This hook drives that contract: it debounces the search box, tracks the sort +
 * page, builds the query string, and returns the paginated envelope. There is NO
 * client-side .slice()/.filter() — the server returns exactly the page shown.
 *
 * Passing `page`/`limit` makes the response the enveloped form, so `data.rows`
 * and `data.total` are always defined here.
 */

export interface Paginated<T> {
  rows: T[];
  total: number;
  page: number;
  limit: number;
}

export interface UseServerListParams {
  /** URL slug mounted at /api/<resource>, e.g. 'parcels'. */
  resource: string;
  /** Base react-query key; the live params are appended to it. */
  queryKey: readonly unknown[];
  defaultPageSize?: number;
  /** Initial sort (column id must be a real table column the server whitelists). */
  defaultSort?: { id: string; desc: boolean };
  /**
   * Scalar equality filters forwarded verbatim (status, re_pp, under_appeal, …).
   * A key whose value is '' / undefined is dropped. Only takes effect when the
   * table actually has that column (server no-ops otherwise).
   */
  filters?: Record<string, string | undefined>;
}

export interface UseServerListResult<T> {
  query: ReturnType<typeof useQuery<Paginated<T>>>;
  rows: T[];
  total: number;
  /** Debounced search value currently applied to the server request. */
  search: string;
  /** Raw (un-debounced) input value — bind this to the search box. */
  searchInput: string;
  setSearchInput: (v: string) => void;
  pageIndex: number;            // 0-based
  setPageIndex: (i: number) => void;
  pageSize: number;
  setPageSize: (n: number) => void;
  pageCount: number;
  sorting: SortingState;
  setSorting: (s: SortingState | ((prev: SortingState) => SortingState)) => void;
}

export function useServerList<T>({
  resource,
  queryKey,
  defaultPageSize = 25,
  defaultSort,
  filters,
}: UseServerListParams): UseServerListResult<T> {
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sorting, setSorting] = useState<SortingState>(defaultSort ? [defaultSort] : []);

  // Drop empty filter values so they don't hit the server as blank params.
  const activeFilters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(filters ?? {})) {
      if (v !== undefined && v !== '') out[k] = v;
    }
    return out;
  }, [filters]);
  const filterKey = JSON.stringify(activeFilters);
  const sort = sorting[0];
  const sortKey = sort ? `${sort.id}:${sort.desc ? 'desc' : 'asc'}` : '';

  // Any change to the query, filters, sort, or page size returns to page 1 —
  // otherwise a narrowed result set could leave the pager on a now-empty page.
  useEffect(() => {
    setPageIndex(0);
  }, [search, filterKey, sortKey, pageSize]);

  const query = useQuery<Paginated<T>>({
    queryKey: [...queryKey, { page: pageIndex, pageSize, search, sortKey, filterKey }],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      params.set('page', String(pageIndex + 1)); // server pages are 1-based
      params.set('limit', String(pageSize));
      if (search) params.set('q', search);
      if (sort) { params.set('sort', sort.id); params.set('dir', sort.desc ? 'desc' : 'asc'); }
      for (const [k, v] of Object.entries(activeFilters)) params.set(k, v);
      return api.get<Paginated<T>>(`/api/${resource}?${params.toString()}`, signal);
    },
    // Keep the previous page visible while the next one loads (no flash-to-empty
    // when paging or typing).
    placeholderData: keepPreviousData,
  });

  const total = query.data?.total ?? 0;
  const rows = query.data?.rows ?? [];
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return {
    query,
    rows,
    total,
    search,
    searchInput,
    setSearchInput,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    pageCount,
    sorting,
    setSorting,
  };
}
